/**
 * Discord-style permission bitfield. Permissions are bigints internally and decimal
 * strings on the wire. The same `computePermissions` runs on the server (enforcement)
 * and on clients (UI gating), so they can never disagree.
 */
export const Permissions = {
    CREATE_INVITE: 1n << 0n,
    KICK_MEMBERS: 1n << 1n,
    BAN_MEMBERS: 1n << 2n,
    ADMINISTRATOR: 1n << 3n,
    MANAGE_CHANNELS: 1n << 4n,
    MANAGE_GUILD: 1n << 5n,
    ADD_REACTIONS: 1n << 6n,
    VIEW_AUDIT_LOG: 1n << 7n,
    PRIORITY_SPEAKER: 1n << 8n,
    STREAM: 1n << 9n,
    VIEW_CHANNEL: 1n << 10n,
    SEND_MESSAGES: 1n << 11n,
    MANAGE_MESSAGES: 1n << 13n,
    EMBED_LINKS: 1n << 14n,
    ATTACH_FILES: 1n << 15n,
    READ_MESSAGE_HISTORY: 1n << 16n,
    MENTION_EVERYONE: 1n << 17n,
    USE_EXTERNAL_EMOJIS: 1n << 18n,
    CONNECT: 1n << 20n,
    SPEAK: 1n << 21n,
    MUTE_MEMBERS: 1n << 22n,
    DEAFEN_MEMBERS: 1n << 23n,
    MOVE_MEMBERS: 1n << 24n,
    USE_VAD: 1n << 25n,
    CHANGE_NICKNAME: 1n << 26n,
    MANAGE_NICKNAMES: 1n << 27n,
    MANAGE_ROLES: 1n << 28n,
    MANAGE_WEBHOOKS: 1n << 29n,
    MANAGE_EMOJIS: 1n << 30n,
    USE_APPLICATION_COMMANDS: 1n << 31n,
    MANAGE_EVENTS: 1n << 33n,
    MANAGE_THREADS: 1n << 34n,
    CREATE_PUBLIC_THREADS: 1n << 35n,
    CREATE_PRIVATE_THREADS: 1n << 36n,
    SEND_MESSAGES_IN_THREADS: 1n << 38n,
    MODERATE_MEMBERS: 1n << 40n,
    CREATE_EVENTS: 1n << 44n,
    SEND_POLLS: 1n << 49n,
    PIN_MESSAGES: 1n << 51n,
};
export const ALL_PERMISSIONS = Object.values(Permissions).reduce((a, b) => a | b, 0n);
/** Permissions granted to @everyone in a freshly created guild. */
export const DEFAULT_EVERYONE_PERMISSIONS = Permissions.VIEW_CHANNEL |
    Permissions.SEND_MESSAGES |
    Permissions.READ_MESSAGE_HISTORY |
    Permissions.ADD_REACTIONS |
    Permissions.ATTACH_FILES |
    Permissions.EMBED_LINKS |
    Permissions.CREATE_INVITE |
    Permissions.CHANGE_NICKNAME |
    Permissions.USE_EXTERNAL_EMOJIS |
    Permissions.CREATE_PUBLIC_THREADS |
    Permissions.CREATE_PRIVATE_THREADS |
    Permissions.SEND_MESSAGES_IN_THREADS |
    Permissions.SEND_POLLS |
    Permissions.USE_APPLICATION_COMMANDS |
    Permissions.CREATE_EVENTS |
    Permissions.CONNECT |
    Permissions.SPEAK |
    Permissions.STREAM |
    Permissions.USE_VAD;
/** Permissions that only make sense guild-wide; channel overwrites cannot grant them. */
const GUILD_ONLY_PERMISSIONS = Permissions.ADMINISTRATOR |
    Permissions.KICK_MEMBERS |
    Permissions.BAN_MEMBERS |
    Permissions.MANAGE_GUILD |
    Permissions.VIEW_AUDIT_LOG |
    Permissions.CHANGE_NICKNAME |
    Permissions.MANAGE_NICKNAMES |
    Permissions.MANAGE_EMOJIS |
    Permissions.MODERATE_MEMBERS;
/** Permissions that are meaningless without the ability to send messages. */
const SEND_DEPENDENT = Permissions.MENTION_EVERYONE | Permissions.ATTACH_FILES | Permissions.EMBED_LINKS;
export function hasPermission(perms, flag) {
    return (perms & flag) === flag;
}
export function parsePermissions(value) {
    if (value === null || value === undefined || value === '')
        return 0n;
    const v = typeof value === 'bigint' ? value : BigInt(value);
    return v & ALL_PERMISSIONS;
}
export function serializePermissions(value) {
    return (value & ALL_PERMISSIONS).toString();
}
export function permissionNames(value) {
    return Object.keys(Permissions).filter((k) => hasPermission(value, Permissions[k]));
}
export const OverwriteType = { ROLE: 0, MEMBER: 1 };
export function computeBasePermissions(ctx) {
    if (ctx.userId === ctx.ownerId)
        return ALL_PERMISSIONS;
    const everyone = ctx.roles.find((r) => r.id === ctx.guildId);
    let perms = everyone?.permissions ?? 0n;
    const memberRoles = new Set(ctx.memberRoleIds);
    for (const role of ctx.roles) {
        if (memberRoles.has(role.id))
            perms |= role.permissions;
    }
    if (hasPermission(perms, Permissions.ADMINISTRATOR))
        return ALL_PERMISSIONS;
    return applyTimeout(ctx, perms);
}
function applyTimeout(ctx, perms) {
    if (ctx.communicationDisabledUntil && ctx.communicationDisabledUntil.getTime() > Date.now()) {
        return perms & (Permissions.VIEW_CHANNEL | Permissions.READ_MESSAGE_HISTORY);
    }
    return perms;
}
/**
 * Compute a member's effective permissions, optionally in a channel with overwrites.
 * Order (same as Discord): base -> @everyone overwrite -> role overwrites (deny then
 * allow, aggregated) -> member overwrite. Owner and ADMINISTRATOR bypass everything.
 */
export function computePermissions(ctx, overwrites) {
    const base = computeBasePermissions(ctx);
    if (base === ALL_PERMISSIONS)
        return ALL_PERMISSIONS;
    if (!overwrites || overwrites.length === 0)
        return finalize(base);
    let perms = base;
    const everyoneOw = overwrites.find((o) => o.type === OverwriteType.ROLE && o.id === ctx.guildId);
    if (everyoneOw) {
        perms &= ~everyoneOw.deny;
        perms |= everyoneOw.allow & ~GUILD_ONLY_PERMISSIONS;
    }
    const memberRoles = new Set(ctx.memberRoleIds);
    let roleAllow = 0n;
    let roleDeny = 0n;
    for (const ow of overwrites) {
        if (ow.type === OverwriteType.ROLE && ow.id !== ctx.guildId && memberRoles.has(ow.id)) {
            roleAllow |= ow.allow;
            roleDeny |= ow.deny;
        }
    }
    perms &= ~roleDeny;
    perms |= roleAllow & ~GUILD_ONLY_PERMISSIONS;
    const memberOw = overwrites.find((o) => o.type === OverwriteType.MEMBER && o.id === ctx.userId);
    if (memberOw) {
        perms &= ~memberOw.deny;
        perms |= memberOw.allow & ~GUILD_ONLY_PERMISSIONS;
    }
    return applyTimeout(ctx, finalize(perms));
}
function finalize(perms) {
    // Without VIEW_CHANNEL a member has no permissions in the channel at all.
    if (!hasPermission(perms, Permissions.VIEW_CHANNEL))
        return 0n;
    if (!hasPermission(perms, Permissions.SEND_MESSAGES))
        perms &= ~SEND_DEPENDENT;
    return perms;
}
/** Highest role position the member holds (@everyone is position 0). */
export function highestRolePosition(ctx) {
    const ids = new Set(ctx.memberRoleIds);
    return ctx.roles.reduce((max, r) => (ids.has(r.id) && r.position > max ? r.position : max), 0);
}
/**
 * Whether `actor` may act on `target` (kick, ban, edit roles, nickname...).
 * The owner can act on anyone; nobody can act on the owner; otherwise the actor's
 * highest role must be strictly above the target's.
 */
export function canActOnMember(guild, actor, target) {
    if (actor.userId === target.userId)
        return false;
    if (target.userId === guild.ownerId)
        return false;
    if (actor.userId === guild.ownerId)
        return true;
    return (highestRolePosition({ roles: guild.roles, memberRoleIds: actor.roleIds }) >
        highestRolePosition({ roles: guild.roles, memberRoleIds: target.roleIds }));
}
/** Whether `actor` may create/edit/assign a role at `rolePosition`. */
export function canManageRole(guild, actor, rolePosition) {
    if (actor.userId === guild.ownerId)
        return true;
    return highestRolePosition({ roles: guild.roles, memberRoleIds: actor.roleIds }) > rolePosition;
}
/**
 * Permissions inside a thread, derived from the parent channel's. Threads have no
 * overwrites of their own: SEND_MESSAGES comes from SEND_MESSAGES_IN_THREADS, private
 * threads are only visible to their members (or thread managers), and locked threads
 * only accept messages from thread managers.
 */
export function computeThreadPermissions(parentPerms, thread, isMember) {
    if (!hasPermission(parentPerms, Permissions.VIEW_CHANNEL))
        return 0n;
    const manage = hasPermission(parentPerms, Permissions.MANAGE_THREADS);
    if (thread.type === 12 && !isMember && !manage)
        return 0n;
    let perms = parentPerms & ~Permissions.SEND_MESSAGES;
    if (hasPermission(parentPerms, Permissions.SEND_MESSAGES_IN_THREADS) &&
        (!thread.locked || manage)) {
        perms |= Permissions.SEND_MESSAGES;
    }
    return finalize(perms);
}
//# sourceMappingURL=permissions.js.map